/**
 * Базовые типы движка прав.
 *
 * Здесь намеренно продублированы Role / AccessLevel / UserStatus вместо импорта
 * из @prisma/client: пакет не имеет зависимостей (см. PERMISSIONS.md §9).
 * Совпадение с БД проверяется тестом `enum-sync.spec.ts` в @tw/db.
 */

export type Role =
  | 'MANAGER_INTERN'
  | 'MANAGER'
  | 'SENIOR_MANAGER'
  | 'OPERATOR_INTERN'
  | 'OPERATOR'
  | 'SENIOR_OPERATOR'
  | 'HR'
  | 'ADMIN';

/**
 * Ступень роли — то, ЧТО сотрудник может делать.
 *
 * Ветка (менеджер, оператор, HR) определяет, над чем он это делает: свои папки
 * аккаунтов, свои вкладки. Права же зависят только от ступени, и это не
 * упрощение ради краткости, а решение: оператор и менеджер отличаются
 * принадлежностью к команде, а не набором кнопок.
 *
 * Разведение ступени и ветки стоит того, чтобы существовать отдельным типом:
 * иначе каждое новое право пришлось бы вписывать в матрицу трижды, и
 * рассинхрон между ветками стал бы вопросом времени.
 */
export type RoleTier = 'INTERN' | 'MEMBER' | 'SENIOR' | 'ADMIN';

export const ROLE_TIER: Readonly<Record<Role, RoleTier>> = {
  MANAGER_INTERN: 'INTERN',
  OPERATOR_INTERN: 'INTERN',

  MANAGER: 'MEMBER',
  OPERATOR: 'MEMBER',
  // HR работает как сотрудник: те же права, свои аккаунты и вкладки.
  HR: 'MEMBER',

  SENIOR_MANAGER: 'SENIOR',
  SENIOR_OPERATOR: 'SENIOR',

  ADMIN: 'ADMIN',
};

/** Ветка роли. Определяет, к каким папкам аккаунтов сотрудник относится. */
export type RoleTrack = 'MANAGER' | 'OPERATOR' | 'HR' | 'ADMIN';

export const ROLE_TRACK: Readonly<Record<Role, RoleTrack>> = {
  MANAGER_INTERN: 'MANAGER',
  MANAGER: 'MANAGER',
  SENIOR_MANAGER: 'MANAGER',

  OPERATOR_INTERN: 'OPERATOR',
  OPERATOR: 'OPERATOR',
  SENIOR_OPERATOR: 'OPERATOR',

  HR: 'HR',
  ADMIN: 'ADMIN',
};

export type AccessLevel = 'READ_ONLY' | 'MANAGE' | 'FULL';

export type ActorStatus = 'ACTIVE' | 'SUSPENDED' | 'DEACTIVATED';

/** Числовой порядок уровней. Нужен для min() и сравнений. */
export const ACCESS_LEVEL_ORDER: Readonly<Record<AccessLevel, number>> = {
  READ_ONLY: 1,
  MANAGE: 2,
  FULL: 3,
};

export function minAccessLevel(a: AccessLevel, b: AccessLevel): AccessLevel {
  return ACCESS_LEVEL_ORDER[a] <= ACCESS_LEVEL_ORDER[b] ? a : b;
}

export function accessLevelAtLeast(actual: AccessLevel, required: AccessLevel): boolean {
  return ACCESS_LEVEL_ORDER[actual] >= ACCESS_LEVEL_ORDER[required];
}

/**
 * Потолок роли. Применяется ПОСЛЕ уровня назначения и всегда его ограничивает.
 *
 * SENIOR_MANAGER получает MANAGE, а не FULL: на MVP удаление доступно только
 * ADMIN (§27 ТЗ). Senior получает более широкий НАБОР прав (управление
 * назначениями), а не более высокий уровень.
 */
export const ROLE_CEILING: Readonly<Record<Role, AccessLevel>> = {
  MANAGER_INTERN: 'READ_ONLY',
  OPERATOR_INTERN: 'READ_ONLY',

  MANAGER: 'MANAGE',
  OPERATOR: 'MANAGE',
  HR: 'MANAGE',

  SENIOR_MANAGER: 'MANAGE',
  SENIOR_OPERATOR: 'MANAGE',

  ADMIN: 'FULL',
};

/**
 * Контекст актора. Собирается один раз на запрос из БД и дальше только читается.
 *
 * Namespace `accountAccess` содержит уровни ИЗ НАЗНАЧЕНИЙ, без применения
 * потолка роли — потолок применяет движок. Так в контексте видно, что именно
 * записано в БД, и рассинхрон легче диагностировать.
 */
export interface ActorContext {
  readonly userId: string;
  readonly role: Role;
  readonly status: ActorStatus;

  /**
   * ADMIN имеет неявный доступ ко всем аккаунтам, включая созданные после
   * сборки контекста. Флаг, а не перечисление id — перечисление устарело бы
   * в момент создания следующего аккаунта.
   */
  readonly hasGlobalAccountAccess: boolean;

  /** telegramAccountId → уровень из активного AccountAssignment. */
  readonly accountAccess: ReadonlyMap<string, AccessLevel>;

  /** Аккаунты во владении (SeniorAccountAssignment). Только для SENIOR_MANAGER. */
  readonly ownedAccountIds: ReadonlySet<string>;
}

export interface Scope {
  readonly telegramAccountId: string;
}

export type DenyReason =
  | 'ACTOR_INACTIVE'
  | 'UNKNOWN_PERMISSION'
  | 'ROLE_LACKS_PERMISSION'
  | 'SCOPE_REQUIRED'
  | 'SCOPE_NOT_APPLICABLE'
  | 'NO_ACCOUNT_ACCESS'
  | 'INSUFFICIENT_ACCESS_LEVEL';

export type Decision =
  | { readonly allowed: true; readonly effectiveLevel: AccessLevel | null }
  | { readonly allowed: false; readonly reason: DenyReason };

/**
 * Как строить SQL-запрос по доступным аккаунтам.
 *
 * Существует, чтобы выборки строились ОТ множества доступных аккаунтов, а не
 * фильтровались постфактум. Фильтрация после выборки ломает пагинацию и
 * счётчики и раскрывает существование чужих чатов через «дырки» в выдаче.
 */
export type AccountScopeFilter =
  | { readonly kind: 'all' }
  | { readonly kind: 'ids'; readonly ids: readonly string[] };
